import { Title } from "@solidjs/meta";
import { A } from "@solidjs/router";
import { HttpStatusCode } from "@solidjs/start";
import { LogoNoText } from "@storytell/core";
import { For, Show } from "solid-js";
import { LABEL_TABS, labelToSlug, normalizeLabels } from "~/routes/(static)/blog/blogLabels";
import type { Post } from "~/routes/(static)/blog/posts";

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

// Frontmatter dates are calendar days; read as UTC so a reader west of Greenwich doesn't see
// the day before.
export const formatPostDate = (iso: string) => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : dateFormatter.format(parsed);
};

export const labelTextClass = "text-xs font-semibold uppercase tracking-wider text-yellow-800 dark:text-accent";

/**
 * The post's cover image, or a typographic one drawn from its title and `coverStat`, so
 * every card has a cover. Sized by its container, so the same cover works in a card and in
 * the index's feature band.
 */
export function PostCover(props: { post: Post; class?: string }) {
  return (
    <div class={`@container aspect-1200/630 overflow-hidden ${props.class ?? ""}`}>
      <Show
        when={props.post.coverImage}
        fallback={
          <div class="flex h-full flex-col justify-between gap-3 bg-panel p-5 @md:p-8">
            <LogoNoText class="h-4 w-auto self-start text-accent @md:h-6" />
            <Show
              when={props.post.coverStat}
              fallback={
                <p class="line-clamp-3 text-xl leading-tight font-bold tracking-tight text-primary-text @md:text-4xl">
                  {props.post.title}
                </p>
              }
            >
              {(stat) => (
                <p class="flex flex-wrap items-baseline gap-x-3 text-primary-text">
                  <span class="text-5xl leading-none font-extrabold tracking-tighter @md:text-7xl">{stat().value}</span>
                  <span class="text-sm text-secondary-text @md:text-base">{stat().label}</span>
                </p>
              )}
            </Show>
          </div>
        }
      >
        {(src) => <img src={src()} alt={props.post.coverImageAlt ?? ""} class="size-full object-cover" />}
      </Show>
    </div>
  );
}

export function PostMetaLine(props: { post: Post }) {
  return (
    <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
      <Show when={normalizeLabels(props.post.labels)[0]}>
        {(label) => <span class={labelTextClass}>{label()}</span>}
      </Show>
      <span class="text-sm text-secondary-text">
        {formatPostDate(props.post.date)} · {props.post.readingTime} min read
      </span>
    </div>
  );
}

function PostCard(props: { post: Post }) {
  return (
    <A href={`/blog/${props.post.slug}`} class="group flex flex-col gap-3.5 no-underline">
      <PostCover post={props.post} class="rounded-xl border border-primary-border" />
      <PostMetaLine post={props.post} />
      <h3 class="text-lg leading-snug font-semibold text-pretty text-primary-text group-hover:text-brand dark:group-hover:text-brand-hover">
        {props.post.title}
      </h3>
      <Show when={props.post.description ?? props.post.excerpt}>
        {(text) => <p class="line-clamp-3 text-[15px] leading-relaxed text-secondary-text">{text()}</p>}
      </Show>
    </A>
  );
}

export function PostGrid(props: { posts: Post[] }) {
  return (
    <div class="grid gap-x-7 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
      <For each={props.posts}>{(post) => <PostCard post={post} />}</For>
    </div>
  );
}

const tabClass = (active: boolean) =>
  `-mb-px shrink-0 border-b-2 pb-3 text-sm no-underline transition-colors ${
    active
      ? "border-accent font-semibold text-primary-text"
      : "border-transparent text-secondary-text hover:text-primary-text"
  }`;

/** All, then each fixed label that has a visible post. `active` is a label slug. */
export function LabelTabs(props: { posts: Post[]; active?: string }) {
  const tabs = () =>
    LABEL_TABS.filter((tab) =>
      props.posts.some((post) => normalizeLabels(post.labels).some((label) => labelToSlug(label) === labelToSlug(tab)))
    );
  return (
    <nav aria-label="Categories" class="scrollbar-hide flex gap-7 overflow-x-auto border-b border-primary-border">
      <A href="/blog" class={tabClass(!props.active)} aria-current={props.active ? undefined : "page"}>
        All
      </A>
      <For each={tabs()}>
        {(tab) => (
          <A
            href={`/blog/${labelToSlug(tab)}`}
            class={tabClass(props.active === labelToSlug(tab))}
            aria-current={props.active === labelToSlug(tab) ? "page" : undefined}
          >
            {tab}
          </A>
        )}
      </For>
    </nav>
  );
}

/** A blog URL with nothing behind it: a mistyped slug, or a draft hidden in production. */
export function BlogNotFound() {
  return (
    <div class="mx-auto max-w-3xl px-4 py-24 text-center sm:px-6 lg:px-8">
      <HttpStatusCode code={404} />
      <Title>Blog · Chief</Title>
      <p class="text-primary-text">This post isn't available.</p>
      <A href="/blog" class="mt-3 inline-block text-sm font-medium text-brand hover:underline">
        Back to the blog
      </A>
    </div>
  );
}
